import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { startOfDay, endOfDay } from 'date-fns';
import OccurrenceEdit from './OccurrenceEdit';
import RoadClosureEdit from './RoadClosure/RoadClosureEdit';
import RcPhasedEdit from './RcPhasedEdit';
import AvalancheControlEdit from './AvalancheControlEdit';
import EventTable from './EventTable';
import { getBreakpoints } from '@awp/helpers/UIHelper';
import { debounce, uuidv4 } from '@awp/helpers/Utils';
import { BREAD_CRUMB_NAMES, EventTypes, FORM_MODE, OfflineFormStatus } from '@awp/constants';
import { selectEventTypes, selectPhasedEventTypes } from '@awp/stores/eventSlice';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { convLocalToIsoString, getUtcToLocalByFormat } from '@awp/helpers/utilsReact';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { AwpContainer } from '../Common/AwpContainer';
import { AvalancheTableFilterContextProvider } from './AvalancheTableFilterContext';

const AvalancheEvent = () => {
  const currentUser = useSelector(selectCurrentUser);
  const location = useLocation();
  const locationState = location?.state;
  const [isRoute, setIsRoute] = useState(
    locationState && locationState.mode && locationState.id && !locationState.fromControl ? true : false,
  );
  const [mode, setMode] = useState(locationState?.mode ? locationState.mode : FORM_MODE.LIST);
  const [fromMenu, setFromMenu] = useState(locationState && locationState.fromMenu ? true : false);
  const [title, setTitle] = useState('Advanced Search');
  const [breakpoints, setBreakpoints] = useState(getBreakpoints());
  const [loading, setLoading] = useState(false);
  const [eventId, setEventId] = useState(null);
  const eventTypes = useSelector(selectEventTypes);
  const phasedEventTypes = useSelector(selectPhasedEventTypes);
  const [startDate, setStartDate] = useState({
    value: new Date(startOfDay(new Date().setDate(new Date().getDate() - 14))),
    disabled: false,
    error: '',
  });
  const [endDate, setEndDate] = useState({ value: new Date(endOfDay(new Date())), disabled: false, error: '' });
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedAvalanchTech, setSelectedAvalancheTech] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [search, setSearch] = useState('');
  const [fullData, setFullData] = useState([]);
  const [eventTypeToActOn, setEventTypeToActOn] = useState(null);
  const [eventTypeToCreate, setEventTypeToCreate] = useState(null);
  const [index, setIndex] = useState(0);
  const [sort, setSort] = useState([{ id: 'timestamp', desc: true }]);
  const [defaultPageSize, setDefaultPageSize] = useState(25);
  const [isProcessed, setIsProcessed] = useState(false);
  const [isDuplicated, setIsDuplicated] = useState(false);
  const [expandedRowEntityId, setExpandedRowEntityId] = useState(null);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  useEffect(() => {
    const handleResize = debounce(() => {
      setBreakpoints(getBreakpoints());
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  /**
   * Finds an event by ID from a nested three-level avlanche event structure (event data).
   * The event may exist at the root level, child level, or grandchild level.
   *
   * @param {Array} eventData - The array of event objects to search through.
   * @param {number|string} evtId - The ID of the event to find.
   * @returns {Object|null} - Returns the matching event object if found, otherwise null.
   */
  const findEvent = (eventData, evtId) => {
    if (!eventData || eventData.length === 0) return null;
    for (const event of eventData) {
      // Check if the current event's id matches
      if (event.id === evtId) {
        event.internal.timestamp = new Date(event.internal.timestamp);
        return event;
      }

      // If the current event has child events, recursively search them
      if (event.ChildIngestionCandidates && event.ChildIngestionCandidates.length > 0) {
        for (const ch of event.ChildIngestionCandidates) {
          const child = JSON.parse(ch.JsonObject);
          child.ChildIngestionCandidates = ch.ChildIngestionCandidates;

          if (child.id === evtId) {
            child.status = ch.Status;
            child.internal.timestamp = new Date(child.internal.timestamp);
            child.internal.event_type = eventTypes?.find((x) => x.value === child.event_type);
            //r.location = getLocation(r);
            return child;
          }
          if (child.ChildIngestionCandidates && child.ChildIngestionCandidates.length > 0) {
            for (const grandc of child.ChildIngestionCandidates) {
              const grandEvent = JSON.parse(grandc.JsonObject);
              grandEvent.ChildIngestionCandidates = grandc.ChildIngestionCandidates;

              if (grandEvent.id === evtId) {
                grandEvent.status = grandc.Status;
                grandEvent.internal.timestamp = new Date(grandEvent.internal.timestamp);
                grandEvent.internal.event_type = eventTypes?.find((x) => x.value === grandEvent.event_type);
                return grandEvent;
              }
            }
          }
        }
      }
    }
    return null; // Return null if no match is found
  };
  const createXnOccurrence = (event) => {
    const id = uuidv4();

    const eventType = eventTypes.find((e) => e.value === EventTypes.AvalancheOccurrence);

    let comment = '';

    let occurrence = {
      id,
      provider: 'INTERNAL_INGESTION_' + currentUser.Env.toUpperCase(),
      trace_id: id,
      parent_trace_id: event?.id,
      //rc_timestamp: event.timestamp,
      timestamp: convLocalToIsoString(new Date(event.timestamp)),
      event_type: eventType.value,
      aval_cntrl_type_cd: event.internal.aval_cntrl_type_cd,
      comment: comment,
      userName: currentUser.UserName,
      userType: currentUser.UserType,
      userId: currentUser.SecurityIdentifierId,
      status: OfflineFormStatus.Duplicated,
      internal: {
        avalanche_area: event.internal.avalanche_area,
        avalanche_path: event.internal.avalanche_path,
        avalanche_program: event.internal.avalanche_program,
        aval_cntrl_type_cd: event.internal.aval_cntrl_type_cd,
        event_type: eventType,
        event_timestamp: new Date(event.timestamp),
        timestamp: new Date(event.timestamp),
      },
      lastUpdated: Date.now(),
    };
    return occurrence;
  };
  const tableProps = {
    title,
    breakpoints,
    setFullData,
    fullData,
    setLoading,
    loading,
    setMode,
    eventId,
    setEventId,
    setEventTypeToActOn,
    eventTypeToCreate,
    setEventTypeToCreate,
    sort,
    setSort,
    setIsDuplicated,
    createXnOccurrence,
    expandedRowEntityId,
    setExpandedRowEntityId,
    isAccordionExpanded,
    setIsAccordionExpanded,
  };
  const editProps = {
    setMode,
    mode: locationState ? (locationState.mode && locationState.id ? locationState.mode : mode) : mode,
    setEventId,
    eventId: locationState && locationState.id ? locationState.id : eventId,
    setLoading,
    loading,
    fullData,
    setEventTypeToActOn,
    setEventTypeToCreate,
    isProcessed,
    isRoute,
    isDuplicated,
    fromMenu,
    findEvent,
    breadcrumbName: BREAD_CRUMB_NAMES.AVALANCHE_EVENTS_LIST,
  };
  if (mode === FORM_MODE.LIST) return <EventTable {...tableProps} />;
  const eventTypeObj =
    mode === FORM_MODE.CREATE ? eventTypeToCreate : !eventTypeToActOn ? eventTypeToCreate : eventTypeToActOn;

  let eventType = locationState && locationState.eventType ? locationState.eventType : eventTypeObj?.value;
  if (phasedEventTypes.find((x) => x.value === eventType)) eventType = EventTypes.PhasedEvent;

  switch (eventType) {
    case EventTypes.AvalancheOccurrence:
      return (
        <OccurrenceEdit {...editProps} isFromControl={locationState && locationState.fromControl ? true : false} />
      );
    case EventTypes.RoadClosure:
      return <RoadClosureEdit {...editProps} />;
    case EventTypes.AvalancheControl:
      return <AvalancheControlEdit {...editProps} createXnOccurrence={createXnOccurrence} />;
    case EventTypes.PhasedEvent:
      return <RcPhasedEdit {...editProps} eventType={eventTypeObj?.value} />;
    default:
      if (eventType) toast.error(`Creation of ${eventType} is not supported yet`, { autoClose: 5000 });
      setMode(FORM_MODE.LIST);
  }
};

export default AvalancheEvent;

export function AvalancheEventWrapper() {
  const location = useLocation();
  const [locationKey, setLocationKey] = useState();

  useEffect(() => {
    setLocationKey(location?.key);
  }, [location]);

  return (
    <AwpContainer>
      <AvalancheTableFilterContextProvider>
        <AvalancheEvent key={locationKey} />
      </AvalancheTableFilterContextProvider>
    </AwpContainer>
  );
}
